import { useCallback, useMemo, useRef, useState, useEffect } from 'react';

const rtcConfig = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

const normalizeTarget = (target, currentUserId = '', peerUserId = '') => {
  const raw = `${target || ''}`.trim();
  if (!raw || raw === 'game' || raw === 'lounge' || raw === 'room') return 'room';
  if (raw.startsWith('dm:')) {
    const ids = raw
      .split(':')
      .slice(1)
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean);
    const unique = [...new Set(ids)];
    const me = `${currentUserId || ''}`.trim();
    const peer = `${peerUserId || ''}`.trim();
    if (me && !unique.includes(me)) unique.push(me);
    if (peer && !unique.includes(peer)) unique.push(peer);
    if (unique.length !== 2) return 'room';
    unique.sort();
    return `dm:${unique[0]}:${unique[1]}`;
  }
  return 'room';
};

const isPrivateTarget = (target) => `${target || ''}`.startsWith('dm:');

const useRoomVoiceChat = ({
  voiceTarget = 'game',
  localUserId,
  localDisplayName,
  voiceEvent,
  joinVoiceRoom,
  leaveVoiceRoom,
  sendVoiceOffer,
  sendVoiceAnswer,
  sendVoiceIceCandidate,
  sendVoiceDecline,
}) => {
  const defaultTarget = useMemo(() => normalizeTarget(voiceTarget, localUserId), [voiceTarget, localUserId]);
  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef(new Map());
  const pendingIceCandidatesRef = useRef(new Map());
  const [isJoined, setIsJoined] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [remotePeers, setRemotePeers] = useState([]);
  const [activeTarget, setActiveTarget] = useState(defaultTarget);
  const [activePipeId, setActivePipeId] = useState('');
  const [incomingCall, setIncomingCall] = useState(null);

  useEffect(() => {
    if (isJoined) return;
    setActiveTarget(defaultTarget);
  }, [defaultTarget, isJoined]);

  const upsertRemotePeer = useCallback((userId, displayName, stream) => {
    const peerId = `${userId || ''}`;
    if (!peerId) return;
    setRemotePeers((prev) => {
      const next = [...prev];
      const idx = next.findIndex((entry) => `${entry.userId}` === peerId);
      const entry = {
        userId: peerId,
        displayName: displayName || 'Player',
        stream,
      };
      if (idx === -1) next.push(entry);
      else next[idx] = { ...next[idx], ...entry };
      return next;
    });
  }, []);

  const removeRemotePeer = useCallback((userId) => {
    const peerId = `${userId || ''}`;
    if (!peerId) return;
    setRemotePeers((prev) => prev.filter((entry) => `${entry.userId}` !== peerId));
  }, []);

  const closePeerConnection = useCallback((userId) => {
    const peerId = `${userId || ''}`;
    const pc = peerConnectionsRef.current.get(peerId);
    if (!pc) return;
    try {
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.close();
    } catch {
      // Ignore close failures.
    }
    peerConnectionsRef.current.delete(peerId);
    pendingIceCandidatesRef.current.delete(peerId);
    removeRemotePeer(peerId);
  }, [removeRemotePeer]);

  const flushPendingIceCandidates = useCallback(async (peerId, pc) => {
    const queued = pendingIceCandidatesRef.current.get(peerId) || [];
    if (!queued.length) return;
    pendingIceCandidatesRef.current.delete(peerId);
    for (const candidate of queued) {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }, []);

  const addIceCandidateSafely = useCallback(async (peerId, pc, candidate) => {
    if (!candidate || !pc) return;
    const hasRemoteDescription = !!pc.remoteDescription;
    if (!hasRemoteDescription) {
      const queued = pendingIceCandidatesRef.current.get(peerId) || [];
      queued.push(candidate);
      pendingIceCandidatesRef.current.set(peerId, queued);
      return;
    }
    await pc.addIceCandidate(new RTCIceCandidate(candidate));
  }, []);

  const createPeerConnection = useCallback((remoteUserId, remoteDisplayName, targetForSignal) => {
    const peerId = `${remoteUserId || ''}`;
    if (!peerId) return null;

    if (peerConnectionsRef.current.has(peerId)) {
      return peerConnectionsRef.current.get(peerId);
    }

    const pc = new RTCPeerConnection(rtcConfig);

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    pc.ontrack = (event) => {
      const [stream] = event.streams || [];
      if (!stream) return;
      upsertRemotePeer(peerId, remoteDisplayName, stream);
    };

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      sendVoiceIceCandidate(peerId, event.candidate, targetForSignal);
    };

    peerConnectionsRef.current.set(peerId, pc);
    return pc;
  }, [sendVoiceIceCandidate, upsertRemotePeer]);

  const createAndSendOffer = useCallback(async (remoteUserId, remoteDisplayName, targetForSignal) => {
    const pc = createPeerConnection(remoteUserId, remoteDisplayName, targetForSignal);
    if (!pc) return;

    const offer = await pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });
    await pc.setLocalDescription(offer);
    sendVoiceOffer(`${remoteUserId}`, offer, targetForSignal);
  }, [createPeerConnection, sendVoiceOffer]);

  const ensureLocalAudio = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    localStreamRef.current = localStream;
    if (isMuted) {
      localStream.getAudioTracks().forEach((track) => {
        track.enabled = false;
      });
    }
    return localStream;
  }, [isMuted]);

  const resetConnections = useCallback(() => {
    peerConnectionsRef.current.forEach((pc) => {
      try {
        pc.close();
      } catch {
        // Ignore close failures.
      }
    });
    peerConnectionsRef.current.clear();
    pendingIceCandidatesRef.current.clear();
    setRemotePeers([]);
  }, []);

  const activateVoiceTarget = useCallback((nextTarget, options = {}) => {
    const force = !!options?.force;
    const normalizedNext = normalizeTarget(nextTarget, localUserId);
    const normalizedCurrent = normalizeTarget(activeTarget, localUserId);

    if (isJoined && normalizedCurrent === normalizedNext && !force) {
      return normalizedCurrent;
    }

    if (isJoined) {
      leaveVoiceRoom(normalizedCurrent);
    }

    resetConnections();
    joinVoiceRoom(normalizedNext);
    setActiveTarget(normalizedNext);
    setActivePipeId('');
    setIsJoined(true);
    return normalizedNext;
  }, [activeTarget, isJoined, joinVoiceRoom, leaveVoiceRoom, localUserId, resetConnections]);

  const joinTarget = useCallback(async (target = 'room') => {
    try {
      setVoiceError('');
      await ensureLocalAudio();
      return activateVoiceTarget(target);
    } catch (err) {
      setVoiceError(err?.message || 'Could not access microphone');
      return null;
    }
  }, [activateVoiceTarget, ensureLocalAudio]);

  const join = useCallback(async () => {
    await joinTarget('room');
  }, [joinTarget]);

  const leave = useCallback(() => {
    if (!isJoined) return;
    leaveVoiceRoom(activeTarget);
    resetConnections();

    const stream = localStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // Ignore track stop failures.
        }
      });
    }
    localStreamRef.current = null;
    setIncomingCall(null);
    setActiveTarget(defaultTarget);
    setActivePipeId('');
    setIsJoined(false);
  }, [activeTarget, defaultTarget, isJoined, leaveVoiceRoom, resetConnections]);

  const callPeer = useCallback(async (remoteUserId, remoteDisplayName = 'Player') => {
    const peerId = `${remoteUserId || ''}`;
    if (!peerId || peerId === `${localUserId || ''}`) return false;
    try {
      setVoiceError('');
      await ensureLocalAudio();
      const privateTarget = normalizeTarget('dm:', localUserId, peerId);
      if (!isJoined || normalizeTarget(activeTarget, localUserId) !== privateTarget) {
        activateVoiceTarget(privateTarget);
      }
      closePeerConnection(peerId);
      await createAndSendOffer(peerId, remoteDisplayName, privateTarget);
      return true;
    } catch (err) {
      setVoiceError(err?.message || 'Could not start private voice call');
      return false;
    }
  }, [activeTarget, activateVoiceTarget, closePeerConnection, createAndSendOffer, ensureLocalAudio, isJoined, localUserId]);

  const acceptIncomingCall = useCallback(async () => {
    if (!incomingCall?.fromUserId || !incomingCall?.sdp) return false;
    try {
      setVoiceError('');
      await ensureLocalAudio();
      const targetForCall = normalizeTarget(incomingCall.target, localUserId, incomingCall.fromUserId);
      const alreadyOnTarget = isJoined && normalizeTarget(activeTarget, localUserId) === targetForCall;
      if (!alreadyOnTarget) {
        activateVoiceTarget(targetForCall);
      }

      closePeerConnection(`${incomingCall.fromUserId || ''}`);
      const pc = createPeerConnection(incomingCall.fromUserId, incomingCall.fromDisplayName || 'Player', targetForCall);
      if (!pc) return false;
      await pc.setRemoteDescription(new RTCSessionDescription(incomingCall.sdp));
      await flushPendingIceCandidates(`${incomingCall.fromUserId || ''}`, pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendVoiceAnswer(incomingCall.fromUserId, answer, targetForCall);
      setIncomingCall(null);
      return true;
    } catch (err) {
      setVoiceError(err?.message || 'Could not accept incoming call');
      return false;
    }
  }, [activeTarget, activateVoiceTarget, closePeerConnection, createPeerConnection, ensureLocalAudio, flushPendingIceCandidates, incomingCall, isJoined, localUserId, sendVoiceAnswer]);

  const declineIncomingCall = useCallback((reason = 'declined') => {
    if (!incomingCall?.fromUserId) return;
    const targetForCall = normalizeTarget(incomingCall.target, localUserId, incomingCall.fromUserId);
    sendVoiceDecline?.(incomingCall.fromUserId, reason, targetForCall);
    setIncomingCall(null);
  }, [incomingCall, localUserId, sendVoiceDecline]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      const stream = localStreamRef.current;
      if (stream) {
        stream.getAudioTracks().forEach((track) => {
          track.enabled = !next;
        });
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!voiceEvent) return;
    const { type, payload = {} } = voiceEvent;
    const eventTarget = normalizeTarget(payload?.target, localUserId, payload?.fromUserId || '');

    const fromUserId = `${payload.fromUserId || ''}`;

    const handleVoiceEvent = async () => {
      if (payload?.pipeId && eventTarget === activeTarget) {
        setActivePipeId(`${payload.pipeId}`);
      }

      if (type === 'voice-room-state') {
        if (eventTarget !== activeTarget) return;
        if (!isJoined) return;
        if (isPrivateTarget(activeTarget)) return;
        const participants = Array.isArray(payload.participants) ? payload.participants : [];
        for (const participant of participants) {
          const participantId = `${participant?.userId || ''}`;
          if (!participantId || participantId === `${localUserId || ''}`) continue;
          await createAndSendOffer(participantId, participant?.displayName || 'Player', activeTarget);
        }
      }

      if (type === 'voice-user-left') {
        if (eventTarget !== activeTarget) return;
        if (!isJoined) return;
        closePeerConnection(payload.userId);
      }

      if (type === 'voice-offer') {
        if (!fromUserId || fromUserId === `${localUserId || ''}`) return;
        if (isPrivateTarget(eventTarget)) {
          setIncomingCall({
            fromUserId,
            fromDisplayName: payload.fromDisplayName || 'Player',
            target: eventTarget,
            sdp: payload.sdp,
          });
          return;
        }
        if (eventTarget !== activeTarget || !isJoined) return;
        const pc = createPeerConnection(fromUserId, payload.fromDisplayName || 'Player', activeTarget);
        if (!pc) return;
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        await flushPendingIceCandidates(fromUserId, pc);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendVoiceAnswer(fromUserId, answer, activeTarget);
      }

      if (type === 'voice-answer') {
        if (eventTarget !== activeTarget) return;
        if (!isJoined) return;
        if (!fromUserId) return;
        const pc = peerConnectionsRef.current.get(fromUserId);
        if (!pc) return;
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        await flushPendingIceCandidates(fromUserId, pc);
      }

      if (type === 'voice-ice-candidate') {
        if (eventTarget !== activeTarget) return;
        if (!isJoined) return;
        if (!fromUserId) return;
        const pc = peerConnectionsRef.current.get(fromUserId);
        if (!pc) {
          const queued = pendingIceCandidatesRef.current.get(fromUserId) || [];
          queued.push(payload.candidate);
          pendingIceCandidatesRef.current.set(fromUserId, queued);
          return;
        }
        await addIceCandidateSafely(fromUserId, pc, payload.candidate);
      }

      if (type === 'voice-decline') {
        if (eventTarget !== activeTarget) return;
        if (!fromUserId) return;
        closePeerConnection(fromUserId);
        setVoiceError(`${payload.fromDisplayName || 'Player'} declined the call.`);
      }
    };

    handleVoiceEvent().catch((err) => {
      setVoiceError(err?.message || 'Voice signaling error');
    });
  }, [
    closePeerConnection,
    addIceCandidateSafely,
    createAndSendOffer,
    createPeerConnection,
    flushPendingIceCandidates,
    activeTarget,
    isJoined,
    localUserId,
    sendVoiceDecline,
    sendVoiceAnswer,
    voiceEvent,
  ]);

  useEffect(() => () => {
    leave();
  }, [leave]);

  return {
    isJoined,
    isMuted,
    voiceError,
    activeTarget,
    activePipeId,
    incomingCall,
    remotePeers,
    join,
    joinTarget,
    leave,
    toggleMute,
    callPeer,
    acceptIncomingCall,
    declineIncomingCall,
    localDisplayName: localDisplayName || 'You',
  };
};

export default useRoomVoiceChat;
